import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PortalLayout from '@/components/layouts/PortalLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { portalAPI } from '@/lib/api';
import { toast } from 'sonner';
import { CheckCircle2, Loader2 } from 'lucide-react';

export default function RequestService() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    site_address: '',
    description: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await portalAPI.submitServiceRequest(formData);
      setSubmitted(true);
      toast.success('Service request submitted successfully!');
    } catch (error) {
      console.error('Error submitting service request:', error);
      toast.error('Failed to submit service request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <PortalLayout>
        <div className="max-w-2xl mx-auto px-4 py-16">
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="text-emerald-600" size={32} />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4" data-testid="success-title">
                Service Request Submitted!
              </h2>
              <p className="text-slate-600 mb-8">
                Thank you for your request. Our team will review it and contact you shortly to confirm the service details.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => navigate('/portal/my-rentals')}
                  className="bg-[#194461] hover:bg-[#091D2E]"
                  data-testid="back-rentals-btn"
                >
                  Back to My Rentals
                </Button>
                <Button 
                  onClick={() => {
                    setSubmitted(false);
                    setFormData({
                      name: '',
                      email: '',
                      phone: '',
                      site_address: '',
                      description: '',
                    });
                  }}
                  variant="outline"
                  data-testid="submit-another-btn"
                >
                  Submit Another Request
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div className="max-w-2xl mx-auto px-4 py-12">
        <Card className="border-slate-200">
          <CardHeader className="pb-6">
            <CardTitle className="text-2xl" data-testid="form-title">Request Extra Service</CardTitle>
            <CardDescription>
              Need an extra service visit or want to extend your rental? Let us know and we'll take care of it.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Your Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Smith"
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="(423) 555-0100"
                    data-testid="input-phone"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="john@example.com"
                  data-testid="input-email"
                />
              </div>

              <div>
                <Label htmlFor="site_address">Site Address *</Label>
                <Input
                  id="site_address"
                  name="site_address"
                  value={formData.site_address}
                  onChange={handleChange}
                  required
                  placeholder="123 Main St, Johnson City, TN 37601"
                  data-testid="input-site-address"
                />
              </div>

              <div>
                <Label htmlFor="description">What do you need? *</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  placeholder="Please describe your service request (extra service visit, rental extension, unit relocation, etc.)"
                  rows={4}
                  data-testid="input-description"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#194461] hover:bg-[#091D2E]"
                disabled={loading}
                data-testid="submit-service-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Service Request'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </PortalLayout>
  );
}
